import { observer } from "mobx-react-lite";
import { useContext, useState } from "react";
import { Chart } from "react-google-charts";
import { StoreContext } from "../stores/rootStore";
import {
  ActionIcon,
  Grid,
  Group,
  Highlight,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import React from "react";
import { PerNodeLatencyResponse } from "../apiResponses/realtimeResponse";
import { IconRefresh } from "@tabler/icons-react";
import {
  HistogramSummary,
  NodeResponseTimes,
} from "../apiResponses/influxResponses";
import TimeRangeSelector from "../components/TimeRangeSelector";

const ResponseTimes: React.FC = observer(() => {
  const rs = useContext(StoreContext);
  const [filter, setFilter] = useState<string>("");
  const [minAvg, setMinAvg] = useState<number>(0);
  const is = rs.influxStore;

  const options = {
    title: "Latency Distribution",
    orientation: "horizontal",
  };

  const perNodeData = is.nodeResponseTimesReq.getData();
  if (perNodeData === null) {
    return <React.Fragment>fetching data</React.Fragment>;
  }

  return (
    <Grid>
      <Grid.Col>
        <Group>
          <TimeRangeSelector />
          <ActionIcon
            variant="filled"
            onClick={() => {
              is.refreshAll();
            }}
          >
            <IconRefresh stroke={2} />
          </ActionIcon>
        </Group>
      </Grid.Col>
      <Grid.Col>
        <Group justify="center" align="end">
          <Title fz={30}>Per Node API Response Times</Title>
          <TextInput
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={"Filter by node id or region"}
            label="Filter by node id or region"
            miw={220}
          />
          <NumberInput
            value={minAvg}
            allowNegative={false}
            allowDecimal={false}
            label="Minimum average to display"
            suffix="ms"
            onChange={(v) => {
              if (typeof v === "number") {
                setMinAvg(v);
                return;
              }
              if (!isNaN(Number(v))) {
                setMinAvg(Number(v));
                return;
              }
            }}
          />
        </Group>
      </Grid.Col>
      {tablesFromPerNodeData(perNodeData, filter, minAvg)}
    </Grid>
  );
});

/*
          <CopyButton value={listOfNodes(perNodeData, true)}>
            {({ copied, copy }) => {
              return (
                <Button color={copied ? "#7033FC" : "#BD6DFF"} onClick={copy}>
                  {copied ? "Copied node list" : "Copy node list"}
                </Button>
              );
            }}
          </CopyButton>
*/

function listOfNodes(
  perNodeData: PerNodeLatencyResponse | null,
  useRegions?: boolean,
): string {
  if (perNodeData === null) {
    return "";
  }

  const nodeList = Object.keys(perNodeData);

  if (!useRegions) {
    return nodeList.join("\n");
  }
  return nodeList.map((n) => n).join("\n");
}

function tablesFromPerNodeData(
  perNodeData: NodeResponseTimes | null,
  filter: string,
  minAvg: number,
) {
  if (perNodeData === null) {
    return null;
  }

  let nodeDatas: {
    chartData: any[];
    node: string;
    total: number;
    warnings: string[];
    summary: HistogramSummary;
  }[] = [];

  const ret = [];
  for (const node in perNodeData) {
    const nodeResponseTimeResp = perNodeData[node];
    let tot = 0;
    const chartData = [
      ["Bin", "Count"],
      ...nodeResponseTimeResp.bins.map((v) => {
        tot += v.count;
        return [v.label, v.count];
      }),
    ];
    nodeDatas.push({
      chartData,
      node,
      total: tot,
      warnings: nodeResponseTimeResp.warnings ?? [],
      summary: nodeResponseTimeResp.summary,
    });
  }

  // This is the syntax to ignore destructured elements in typescript
  nodeDatas.sort((a, b) => b.summary.p90 - a.summary.p90);
  nodeDatas = nodeDatas.filter(
    (v) => v.node.includes(filter) && v.summary.median >= minAvg,
  );

  for (const { node, chartData, summary, total } of nodeDatas) {
    const options = {
      title: `Latency Distribution`,
      chartArea: {
        height: "80%",
      },
    };
    ret.push(
      <Grid.Col span={6}>
        <Stack align="center" gap={0}>
          <Stack align="left" gap={0}>
            <Highlight highlight={filter} component="span" ff="mono" size="sm">
              {`${node}`}
            </Highlight>
            <Text>Requests Served: {total}</Text>
            <Text>Median: {summary.median}ms</Text>
            <Text>P75: {summary.third_quartile}ms</Text>
            <Text>P90: {summary.p90}ms</Text>
          </Stack>
          <Chart
            chartType="BarChart"
            data={chartData}
            height="300px"
            options={options}
          />
        </Stack>
      </Grid.Col>,
    );
  }
  return ret;
}

export default ResponseTimes;
